import { inEnum, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { StaticContentSizing } from "../../../lib/dynamicscaler/ContentSizing.js";
import { parseSizeSpecification, VideoContainerType, VideoFormatRawData } from "../../../model/video/VideoFormat.js";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";


export type SizePreferenceOptions = {

    size: number,
    dimension: "width" | "height" | "significant" ,
    fallback: "lowest" | "lower" | "higher" | "highest"
    
}

export enum GenericFormatLabel {

    p240 = "240p",
    p360 = "360p",
    p480 = "480p",
    p720 = "720p",
    p1080 = "1080p",
    p2160 = "2160p",
    UNSPECIFIED = "-"

}



export function isGenericFormatLabel(a: any): a is GenericFormatLabel {
    return isString(a) && inEnum(a,GenericFormatLabel);
}


export type ParsedFormat = {
    parsedSize: StaticContentSizing,
    url: string,
    originalDef: VideoFormatRawData,
    label: GenericFormatLabel,
    mainSize: number
}

export interface SizePreferencePolicy {

    pickFromFormats(fmt: ParsedFormat[]): ParsedFormat | null;
    updateTargetSize(size: number): void;
    getTargetSize(): number;

}


function formatLabeler(pSize: StaticContentSizing): GenericFormatLabel {
    const s = Math.min(pSize.height,pSize.width);
    if(s <= 300) return GenericFormatLabel.p240;
    else if ( s <= 420) return GenericFormatLabel.p360;
    else if ( s <= 600) return GenericFormatLabel.p480;
    else if ( s <= 900) return GenericFormatLabel.p720;
    else if ( s <= 1620) return GenericFormatLabel.p1080;
    else return GenericFormatLabel.p2160;
}

function mainSize(pSize: StaticContentSizing): number {
    const s = Math.min(pSize.height,pSize.width);
    if(s <= 300) return 240;
    else if ( s <= 420) return 360;
    else if ( s <= 600) return 480;
    else if ( s <= 900) return 720;
    else if ( s <= 1620) return 1080;
    else return 2160;
}

export function getParsedFormats(container: VideoContainerType, vinfo: VideoInfoRawData): ParsedFormat[] {
    return vinfo.format.filter(e => e.container == container).map(x => {
        const psize = parseSizeSpecification(x.size);    
        const usize = psize && psize instanceof StaticContentSizing?psize:null;
        return {
            parsedSize: usize,
            url: x.resourceUrl,
            originalDef: x,
            label: usize?formatLabeler(usize):GenericFormatLabel.UNSPECIFIED,
            mainSize: usize?mainSize(usize): null,
        }
    }).sort((a,b) => {
        return b.parsedSize.getArea() - a.parsedSize.getArea()
     } );

}


class SizePreferencePolicyDefaultImpl implements SizePreferencePolicy {
    
    
    private current: SizePreferenceOptions;


    constructor(startingPreferences: SizePreferenceOptions) {
        this.current = startingPreferences;
    }  

    
    pickFromFormats(formats: ParsedFormat[]): ParsedFormat | null {
        let output: ParsedFormat | null = null;
        if(formats && formats.length > 0) {
            const sorted = formats.filter((e) => e.parsedSize).sort((a,b) => a.parsedSize.getArea() - b.parsedSize.getArea());
            const withDistances: {w: number, h: number, s: number, p: ParsedFormat}[] = sorted.map((a) => {
                return {
                    w: a.parsedSize.width - this.current.size,
                    h: a.parsedSize.height - this.current.size,
                    s: Math.min(a.parsedSize.width, a.parsedSize.height) - this.current.size, 
                    p: a
                }
            });

            const extractor: (el: {w: number, h: number, s: number, p: ParsedFormat}) => number = (t) => {
                if(this.current.dimension == "width") return t.w;
                else if (this.current.dimension == "height") return t.h;
                else return t.s;
            }
           
            let grossOutput = withDistances.find(el => extractor(el) == 0);
            
            if(!grossOutput) {
                if(this.current.fallback == "lowest") {
                    grossOutput = withDistances.shift();
                }
                else if (this.current.fallback == "highest") {
                    grossOutput = withDistances.pop();
                }
                else {
                    const sPositives = withDistances.filter(x => extractor(x) >= 0).sort((a,b) => extractor(a) - extractor(b))
                    const sNegatives = withDistances.filter(x => extractor(x) < 0).sort((a,b) => extractor(b) - extractor(a))

                    const prio = this.current.fallback == "lower"?[sNegatives,sPositives]:[sPositives,sNegatives];
                    grossOutput = prio[0].shift();
                    if(!grossOutput) grossOutput = prio[1].shift();
                }
            }

            output = grossOutput?.p;
        }
        return output;
    }

 

    updateTargetSize(size: number): void {
        this.current.size = size;
    }

    getTargetSize(): number {
        return this.current.size;
    }
    

}



export function createSizePreference(s: SizePreferenceOptions): SizePreferencePolicy {
    return new SizePreferencePolicyDefaultImpl(s);
}